<template>
  <base-dialog
    data-testid="legalTermsDialog.dialog"
    dialog-id="legalTermsDialog"
    width="550px"
    :cancelable="false"
    :banner="showBanner"
    :title="'Just one more step'"
    @complete-dialog="emitContinue"
    @cancel-dialog="emitContinue"
  >
    <template #subHeader>
      <div class="terms-dialog">
        <h3>Please review the following terms before continuing:</h3>
      </div>
    </template>

    <template #content>
      <div class="terms-dialog">
        <div class="terms-group">
          <div v-for="(item, index) in mandatoryTerms" :key="'mandatory-' + index" class="terms-checkbox-item">
            <v-checkbox
              v-model="item.accepted"
              :label="item.description"
              color="primary"
              hide-details
              density="compact"
              :disabled="false"
            >
              <template #label>
                <span class="terms-label-text"
                  >I have read and accept the
                  <span v-if="item.url" class="terms-link" @click="openLink(item.url)">{{ item.description }}</span>
                  <span v-else>{{ item.description }}</span>
                </span>
              </template>
            </v-checkbox>
          </div>
        </div>

        <div class="terms-group">
          <div v-for="(item, index) in optionalTerms" :key="'optional-' + index" class="terms-checkbox-item">
            <v-checkbox
              v-model="item.accepted"
              color="primary"
              hide-details
              density="compact"
              :disabled="false"
              class="terms-checkbox-item"
            >
              <template #label>
                <span class="terms-label-text">
                  <span v-if="item.url" class="terms-link clickable" @click="openLink(item.url)">{{
                    item.description
                  }}</span>
                  <span v-else>
                    <p v-for="line in lines(item.description)" :key="line">
                      {{ line }}
                    </p></span
                  >
                </span>
              </template>
            </v-checkbox>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <v-btn-dialog-action-CTA
        data-testid="legalTermsDialog.ok-button"
        :disabled="!allMandatoryAccepted"
        @click="emitContinue"
        >Confirm &amp; Proceed</v-btn-dialog-action-CTA
      >
    </template>
  </base-dialog>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref } from "vue";
import { TermsAndConditionsService } from "../Editor/services/terms/terms-and-conditions-service";
import BaseDialog from "../shared/BaseDialog.vue";
import { LegalTerms } from "../Authentication/models/legal-terms";

const termsAndConditionsService: TermsAndConditionsService = inject("termsAndConditionsService");

const terms = ref<LegalTerms[]>([]);

const props = withDefaults(
  defineProps<{
    openLink?: (url: string) => void;
    showBanner?: boolean;
  }>(),
  {
    showBanner: true,
    openLink: (url: string) => window.open(url, "_blank"),
  }
);

const emit = defineEmits(["termsAccepted"]);

function emitContinue() {
  if (allMandatoryAccepted.value) {
    termsAndConditionsService.accept(terms.value).then(() => {
      emit("termsAccepted");
    });
  }
}

function lines(text: string): string[] {
  return text.split("\n");
}

const allMandatoryAccepted = computed(
  () => terms.value.every((term) => !term.mandatory || term.accepted) && terms.value.length > 0
);

const mandatoryTerms = computed(() => terms.value.filter((item) => item.mandatory));

const optionalTerms = computed(() => terms.value.filter((item) => !item.mandatory));

onBeforeMount(() => {
  termsAndConditionsService.check().then((fetchedTerms: LegalTerms[]) => {
    terms.value = fetchedTerms.map((term) => ({
      ...term,
      accepted: term.defaultChoice,
    }));
  });
});
</script>

<style scoped>
.terms-dialog {
  padding: 10px;
}
.terms-group {
  margin-bottom: 25px;
}
.terms-checkbox-item {
  display: flex;
  align-items: flex-start;
  margin: 5px 0;
}
.terms-checkbox-item a {
  color: #003366;
  text-decoration: none;
}
.terms-label-text {
  display: inline-block;
  text-align: left;
  margin-left: 5px;
}
.terms-link {
  font-weight: bolder;
}
.footer {
  align-items: center;
}
</style>
